/**
 * FastTrackButton — "Onboarding express" one-click shortcut.
 *
 * For candidates the recruiter already knows and wants to skip the
 * interview/immersion funnel: forces status → validated on the backend,
 * which auto-sends the onboarding invite email + magic link.
 *
 * Confirms with a modal so we don't accidentally validate the wrong
 * candidate — the action IS reversible via status_history restore but a
 * light guard is still worth the friction.
 */
import * as Dialog from "@radix-ui/react-dialog";
import { motion } from "framer-motion";
import { Rocket, X } from "lucide-react";
import { useState } from "react";
import { toast } from "sonner";

import { api } from "../../lib/api";
import { useEmailPreview } from "../../lib/emailPreview";
import { hasAnyRole } from "../../lib/roles";
import { useAuth } from "../../lib/auth";

const ALLOWED_ROLES = ["admin", "pre_recruteur"];
// Statuses where the shortcut still makes sense (we hide it when it's
// obviously not applicable).
const HIDE_STATUSES = new Set(["hired", "rejected"]);

export default function FastTrackButton({
    candidate,
    variant = "chip",              // "chip" | "cta"
    className = "",
    onDone,                        // (updatedCandidate) => void
    dataTestid,
}) {
    const { user } = useAuth();
    const emailPreview = useEmailPreview();
    const [open, setOpen] = useState(false);
    const [comment, setComment] = useState("");
    const [submitting, setSubmitting] = useState(false);

    if (!user || !hasAnyRole(user, ...ALLOWED_ROLES)) return null;
    if (!candidate || HIDE_STATUSES.has(candidate.status)) return null;

    const alreadyValidated = ["validated", "onboarding"].includes(candidate.status);

    /** Actual server call — used both directly (fallback) and by the
     * preview modal's onConfirm callback. */
    const performFastTrack = async () => {
        const { data } = await api.post(
            `/candidates/${candidate.id}/fast-track-onboarding`,
            { comment: comment.trim() || null },
        );
        toast.success(
            alreadyValidated
                ? "Onboarding renvoyé — le candidat va recevoir un nouveau lien"
                : "Onboarding express lancé — le candidat va recevoir son dossier",
        );
        setOpen(false);
        setComment("");
        onDone?.(data);
        return data;
    };

    const submit = async (e) => {
        e?.preventDefault?.();
        setSubmitting(true);
        try {
            // Open the email preview modal FIRST — the user can see the exact
            // onboarding invite that will be sent, then click "Envoyer" to
            // trigger the real POST. Nothing is sent until they confirm.
            emailPreview.open({
                template: "onboarding_invite",
                candidateId: candidate.id,
                title: alreadyValidated ? "Renvoyer le dossier — aperçu email" : "Onboarding express — aperçu email",
                description: alreadyValidated
                    ? "Voici le dossier d'embauche qui sera renvoyé au candidat. Confirmez pour l'envoyer."
                    : "Voici l'email d'onboarding qui sera envoyé au candidat avec son lien magique. Confirmez pour lancer le process.",
                confirmLabel: alreadyValidated ? "Relancer maintenant" : "Lancer l'onboarding",
                onConfirm: performFastTrack,
            });
            setSubmitting(false);
        } catch (err) {
            const msg = err?.response?.data?.detail || "Impossible d'ouvrir l'aperçu email";
            toast.error(msg);
            setSubmitting(false);
        }
    };

    const label = alreadyValidated ? "Relancer onboarding" : "Onboarding express";

    const triggerCls = variant === "cta"
        ? `h-10 inline-flex items-center gap-1.5 rounded-full px-4 bg-gradient-to-br from-shariti-orange to-[#d66528] text-white hover:brightness-110 font-extrabold text-sm transition shadow-btn ${className}`
        : `h-9 inline-flex items-center gap-1.5 rounded-full px-3.5 border-2 border-shariti-orange text-shariti-orange hover:bg-shariti-orange hover:text-white font-bold text-xs transition ${className}`;

    return (
        <Dialog.Root open={open} onOpenChange={setOpen}>
            <Dialog.Trigger asChild>
                <button
                    type="button"
                    data-testid={dataTestid || `fast-track-btn-${candidate.id}`}
                    onClick={(e) => e.stopPropagation()}
                    className={triggerCls}
                    title="Sauter entretien + immersion et envoyer le dossier d'onboarding immédiatement"
                >
                    <Rocket className="h-3.5 w-3.5" />
                    <span>{label}</span>
                </button>
            </Dialog.Trigger>

            <Dialog.Portal>
                <Dialog.Overlay className="fixed inset-0 z-[70] bg-shariti-black/50 backdrop-blur-sm data-[state=open]:animate-in data-[state=open]:fade-in" />
                <Dialog.Content
                    onClick={(e) => e.stopPropagation()}
                    data-testid="fast-track-dialog"
                    className="fixed left-1/2 top-1/2 z-[71] w-[92vw] max-w-md -translate-x-1/2 -translate-y-1/2 rounded-2xl bg-white shadow-2xl focus:outline-none data-[state=open]:animate-in data-[state=open]:fade-in data-[state=open]:zoom-in-95"
                >
                    <motion.div layout>
                        <div className="px-6 py-5 border-b border-slate-100 flex items-start justify-between gap-3">
                            <div>
                                <div className="flex items-center gap-2">
                                    <div className="h-9 w-9 rounded-full bg-gradient-to-br from-shariti-orange to-[#d66528] flex items-center justify-center">
                                        <Rocket className="h-4 w-4 text-white" />
                                    </div>
                                    <Dialog.Title className="text-lg font-extrabold text-shariti-black">
                                        {alreadyValidated ? "Renvoyer le dossier d'onboarding" : "Onboarding express"}
                                    </Dialog.Title>
                                </div>
                                <Dialog.Description className="mt-1.5 text-xs text-slate-600 font-semibold">
                                    {alreadyValidated
                                        ? "Un nouveau lien va être généré et le candidat recevra son dossier par email."
                                        : "Le candidat va sauter entretien + immersion. Son statut passera directement à « Validé » et le dossier d'onboarding lui sera envoyé par email tout de suite."}
                                </Dialog.Description>
                            </div>
                            <Dialog.Close asChild>
                                <button
                                    type="button"
                                    aria-label="Fermer"
                                    className="h-8 w-8 shrink-0 rounded-full hover:bg-slate-100 inline-flex items-center justify-center text-slate-500"
                                >
                                    <X className="h-4 w-4" />
                                </button>
                            </Dialog.Close>
                        </div>

                        <form onSubmit={submit} className="px-6 py-5 space-y-4">
                            <div className="rounded-xl bg-shariti-cream/60 border border-shariti-orange/20 p-3">
                                <div className="text-[10px] font-black uppercase tracking-widest text-shariti-orange">Candidat</div>
                                <div className="mt-0.5 text-base font-extrabold text-shariti-black">
                                    {candidate.first_name} {candidate.last_name}
                                </div>
                                <div className="text-xs font-semibold text-slate-600">
                                    {candidate.email || "Email manquant"}
                                    {candidate.phone && <> · {candidate.phone}</>}
                                </div>
                            </div>

                            <div>
                                <label
                                    htmlFor="fast-track-comment"
                                    className="text-[10px] font-black uppercase tracking-widest text-slate-500"
                                >
                                    Motif (optionnel, tracé dans l'historique)
                                </label>
                                <textarea
                                    id="fast-track-comment"
                                    data-testid="fast-track-comment"
                                    value={comment}
                                    onChange={(e) => setComment(e.target.value)}
                                    rows={3}
                                    placeholder="Ex. Candidat déjà connu, ancien vendeur Shariti, référé par Marc, …"
                                    className="mt-1.5 w-full rounded-xl border-2 border-slate-200 focus:border-shariti-orange focus:ring-0 px-3 py-2 text-sm resize-none transition"
                                />
                            </div>

                            <div className="flex items-center justify-end gap-2 pt-2">
                                <Dialog.Close asChild>
                                    <button
                                        type="button"
                                        className="h-10 px-4 rounded-full bg-slate-100 hover:bg-slate-200 text-slate-700 font-bold text-sm transition"
                                    >
                                        Annuler
                                    </button>
                                </Dialog.Close>
                                <button
                                    type="submit"
                                    data-testid="fast-track-confirm"
                                    disabled={submitting || !candidate.email}
                                    title={!candidate.email ? "Ce candidat n'a pas d'email" : undefined}
                                    className="h-10 px-4 rounded-full bg-gradient-to-br from-shariti-orange to-[#d66528] text-white hover:brightness-110 font-extrabold text-sm inline-flex items-center gap-1.5 shadow-btn transition disabled:opacity-50 disabled:cursor-not-allowed"
                                >
                                    <Rocket className="h-3.5 w-3.5" />
                                    {submitting ? "Envoi en cours…" : (alreadyValidated ? "Relancer" : "Lancer l'onboarding")}
                                </button>
                            </div>
                        </form>
                    </motion.div>
                </Dialog.Content>
            </Dialog.Portal>
        </Dialog.Root>
    );
}
